import {
  ApiClientError,
  clientInfoHeaders,
  createApiClient,
  CSRF_COOKIE,
  CSRF_HEADER,
  documentPdfUrl,
  newRequestId,
  productionListXlsxUrl,
  readCookie,
  REQUEST_ID_HEADER,
  unwrap,
  type ApiClient,
} from '@sm/api';
import { bcp47, isLangCode } from '@sm/i18n';

import { env } from '@/env';

import { APP_BUILD, APP_VERSION } from './observability';

/** Rute koje ne traže prijavu (401 na njima ne preusmjerava). */
export const PUBLIC_PATHS = [
  '/prijava',
  '/registracija',
  '/zaboravljena-lozinka',
  '/reset-lozinke',
  '/potvrda-email',
  '/poziv',
  '/otkazi-brisanje',
  '/promjena-email',
  '/auth/callback',
  '/povezivanje-naloga',
  // Oblast platforme ima vlastitu sesiju; 401 API-ja firme tu ne vodi na prijavu panela.
  '/platforma',
] as const;

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

type UnauthorizedHandler = (err: ApiClientError) => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Router registruje handler (preusmjeravanje na /prijava) pri pokretanju. */
export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  unauthorizedHandler = fn;
}

function currentLang(): string {
  try {
    const l = localStorage.getItem('sm-lang');
    return bcp47(isLangCode(l) ? l : 'sr');
  } catch {
    return 'sr-Latn';
  }
}

export const api: ApiClient = createApiClient({
  baseUrl: env.apiUrl,
  credentials: 'include',
  getCsrfToken: () => readCookie(CSRF_COOKIE),
  getLanguage: currentLang,
  // X-Request-Id (UUIDv4 po zahtjevu), X-Client, X-App-Version, X-App-Build (docs/observability.md).
  clientInfo: { client: 'web', appVersion: APP_VERSION, appBuild: APP_BUILD },
  // Kasno vezivanje: MSW i testovi zamjenjuju globalThis.fetch nakon učitavanja modula.
  fetch: (input, init) => globalThis.fetch(input, init),
  onUnauthorized: (err) => {
    if (typeof window === 'undefined') return;
    if (isPublicPath(window.location.pathname)) return;
    unauthorizedHandler?.(err);
  },
});

export { ApiClientError, unwrap };

/** Korelaciona zaglavlja za ručne `fetch` pozive (preuzimanje fajlova, multipart upload). */
function correlationHeaders(): Record<string, string> {
  return {
    [REQUEST_ID_HEADER]: newRequestId(),
    ...clientInfoHeaders({ client: 'web', appVersion: APP_VERSION, appBuild: APP_BUILD }),
  };
}

/**
 * Preuzima binarni sadržaj sa API-ja (cookie + CSRF nisu potrebni za GET) i otvara dijalog za snimanje.
 */
export async function downloadFile(url: string, fallbackName: string): Promise<void> {
  let res: Response;
  const corr = correlationHeaders();
  try {
    res = await fetch(url, {
      credentials: 'include',
      headers: { 'Accept-Language': currentLang(), ...corr },
    });
  } catch (cause) {
    throw ApiClientError.network(cause, corr[REQUEST_ID_HEADER]);
  }
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null);
    if (res.status === 401 && !isPublicPath(window.location.pathname)) {
      unauthorizedHandler?.(ApiClientError.fromBody(401, body));
    }
    throw ApiClientError.fromBody(res.status, body, res.headers.get('X-Request-Id') ?? undefined);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const name = match?.[1] ? decodeURIComponent(match[1]) : fallbackName;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

export const urls = {
  documentPdf: (id: string, download = false) => documentPdfUrl(env.apiUrl, id, download),
  productionListXlsx: (orderId: string, kind: 'cutting' | 'glass' | 'hardware' | 'shutters') =>
    productionListXlsxUrl(env.apiUrl, orderId, kind),
};

/** Ručni POST sa CSRF zaglavljem (multipart upload loga). */
export async function postMultipart<T>(path: string, form: FormData): Promise<T> {
  const corr = correlationHeaders();
  const headers: Record<string, string> = { 'Accept-Language': currentLang(), ...corr };
  const csrf = readCookie(CSRF_COOKIE);
  if (csrf) headers[CSRF_HEADER] = csrf;
  let res: Response;
  try {
    res = await fetch(`${env.apiUrl.replace(/\/+$/, '')}/v1${path}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: form,
    });
  } catch (cause) {
    throw ApiClientError.network(cause, corr[REQUEST_ID_HEADER]);
  }
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok)
    throw ApiClientError.fromBody(
      res.status,
      body,
      res.headers.get(REQUEST_ID_HEADER) ?? undefined,
    );
  return body as T;
}
